/**
 * UTC-safe date helpers for the Fluxo Gerencial page.
 *
 * Why UTC: backend stores `Data` as UTC midnight (2026-04-02T00:00:00Z) for
 * the calendar day the user intends. If the frontend formats with the local
 * timezone, BR users see the previous day (UTC-3 → 21:00 of the previous day).
 *
 * Use these helpers — NEVER `format(d, 'dd/MM/yyyy')` or `new Date(s + 'T00:00:00')` —
 * anywhere a date represents a calendar day (no time component meaning).
 */

/** "2026-04-02T00:00:00Z" or Date → "02/04/2026" using UTC components. */
export function formatDateBR(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (!d || !Number.isFinite(d.getTime())) return '—'
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const yyyy = d.getUTCFullYear()
  return `${dd}/${mm}/${yyyy}`
}

/** Date → "yyyy-MM-dd" using UTC, ready for `<input type="date">`. */
export function toDateInputValue(value: Date | null | undefined): string {
  if (!value || !Number.isFinite(value.getTime())) return ''
  const yyyy = value.getUTCFullYear()
  const mm = String(value.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(value.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * "yyyy-MM-dd" (from a date input or URL) → Date at UTC midnight.
 * Returns null if the string is malformed or yields an invalid date.
 *
 * IMPORTANT: parsing as UTC (not local) ensures the Date represents the same
 * calendar day everywhere — the backend stores UTC midnight, this matches.
 */
export function parseDateInputValue(s: string): Date | null {
  if (!s) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const d = new Date(s + 'T00:00:00Z')
  return Number.isFinite(d.getTime()) ? d : null
}
